import * as PIXI from "pixi.js";
import TickerTimeout from "../../utils/TickerTimeout";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Settings } from "../../settings";

import { formatToCustomLocale } from "../../utilities";
import { FONT_STYLES, scaleStyle } from "../../fontStyles";

import PotCollectionValueTraveler from "./PotCollectionValueTraveler";
import { sortGridOrder } from "./potCollectionGridOrder";
import { getSymbolZIndex } from "../../SymbolData";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";

// ─── Animation Name Constants (from cs.json) ────────────────────────────────

const MYSTERY_ANIMS = {
  SHUFFLE: "CM_Red/jp_shuffle_red_cm",
  STOP_MINI: "CM_Red/jp_shuffle_red_cm_mini_stop",
  STOP_MINOR: "CM_Red/jp_shuffle_red_cm_minor_stop",
  STOP_MAJOR: "CM_Red/jp_shuffle_red_cm_major_stop",
  TRANSFORM_MINI: "CM_Red/jp_transform_red_cm_mini",
  TRANSFORM_MINOR: "CM_Red/jp_transform_red_cm_minor",
  TRANSFORM_MAJOR: "CM_Red/jp_transform_red_cm_major",
};

const DOUBLE_ANIMS = {
  ACTIVATE: "CD_Green/activate_green_cd",
  LOOP_MULTIPLY: "CD_Green/loop_multiply_green_cd",
  ACTIVATE_VALUE: "CD_Green/activate_value_green_cd",
  IDLE: "CD_Green/idle_cd",
};

const COLLECT_ANIMS = {
  ACTIVATE: "CC_Blue/activate_blue_cc",
  UPDATE_VALUE: "CC_Blue/update_value_blue_cc",
  IDLE: "CC_Blue/idle_cc",
};

// Tier name mapping for Mystery
const TIER_TO_STOP = {
  mini: MYSTERY_ANIMS.STOP_MINI,
  minor: MYSTERY_ANIMS.STOP_MINOR,
  major: MYSTERY_ANIMS.STOP_MAJOR,
};

const TIER_TO_TRANSFORM = {
  mini: MYSTERY_ANIMS.TRANSFORM_MINI,
  minor: MYSTERY_ANIMS.TRANSFORM_MINOR,
  major: MYSTERY_ANIMS.TRANSFORM_MAJOR,
};

// Tier → jackpot symbol ID (for swapping to j spine after transform)
const TIER_TO_SYMBOL_ID = {
  mini: 17,   // c_mini
  minor: 18,  // c_minor
  major: 19,  // c_major
};

// Zeus symbolId → feature type

// ─── Timing Constants ────────────────────────────────────────────────────────

const VALUE_TRAVEL_DURATION = 300;  // trail duration (ms) — single knob controls speed

// Trail duration is always VALUE_TRAVEL_DURATION regardless of mode.
// Fast mode (turbo/slamStop) fires trails simultaneously — that IS the speedup.
// Super turbo (future) may add actual duration scaling.

// Slots that make up the "2×" label on the cs spine (bone cd_2X, skin cd)
// These are the ONLY slots shown when creating a traveling 2x clone for Double trails
const DOUBLE_2X_SLOTS = new Set([
  "cd_2", "cd_2_glow", "cd_2_shadow",
  "cd_x", "cd_x_glow", "cd_x_shadow",
]);
/**
 * PotCollectionFeatureExecutor
 *
 * Executes the three feature types (Mystery, Double, Collect) during the
 * Pot Collection bonus game. Processes `screen_progress` actions from the BE
 * and orchestrates spine animations + value text updates.
 *
 * GDD: §15.1 (Mystery), §15.2 (Double), §15.3 (Collect), §15.4 (Combinations)
 *
 * Key responsibility: Given a screen_progress action, play the correct sequence
 * of animations and update coin values on the grid.
 */
class PotCollectionFeatureExecutor {
  /**
   * @param {Object} params
   * @param {Object} params.overlayReelRefs - PC overlay reel refs (for spine animations)
   * @param {Object} params.reelRefs - PC static reel refs (for texture updates)
   * @param {PIXI.Container} params.collectionLayerContainer - Container for value travelers
   * @param {number} params.symbolWidth - Current symbol width
   * @param {number} params.symbolHeight - Current symbol height
   * @param {number} params.reelSpacing - Current reel spacing
   */
  constructor({
    overlayReelRefs,
    reelRefs,
    collectionLayerContainer,
    symbolWidth,
    symbolHeight,
    reelSpacing,
  }) {
    this.overlayReelRefs = overlayReelRefs;
    this.reelRefs = reelRefs;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;

    // Reused scratch object for _getLiveDimensions — read immediately by every
    // caller (never retained), so avoids a per-frame {w,h} allocation per traveler.
    this._liveDims = { symbolWidth: symbolWidth, symbolHeight: symbolHeight };

    this.valueTraveler = collectionLayerContainer
      ? new PotCollectionValueTraveler(collectionLayerContainer, symbolWidth, symbolHeight, () => this._getLiveDimensions())
      : null;

    this.collectionLayerContainer = collectionLayerContainer;

    this._cancelled = false;
    this._spinEngine = null; // Set via setSpinEngine() for reactive slamStop

    // Counter ticker management (mirrors RC counterTickersRef)
    this._counterTickers = new Map(); // key → tickerFn
    this._flippedCoins = new Set(); // Track flipped jackpot coins — flip once only
    this._previousValues = new Map(); // key → last known value for animation start
    this._pendingMysteryTransforms = new Map(); // key → Promise (fire-and-forget transform+swap)

    // Timer leak prevention — track TickerTimeouts for cleanup on cancel
    this._pendingTimers = new Set();
    // Spine listener leak prevention
    this._pendingListeners = [];
  }

  /**
   * Set the SpinEngine reference for reactive slamStop checks.
   * Called by ScreenProgressProcessor before feature execution.
   */
  setSpinEngine(spinEngine) {
    this._spinEngine = spinEngine;
  }

  /**
   * Read live symbolWidth/Height from any overlay controller — always current
   * from React resize effects, even when the executor's cached values are stale.
   */
  _getLiveDimensions() {
    const dims = this._liveDims;
    const refs = this.overlayReelRefs?.current;
    if (refs) {
      for (let r = 0; r < refs.length; r++) {
        const ctrl = refs[r]?.getSymbolRef?.(0)?.getController?.();
        if (ctrl?.symbolHeight) {
          dims.symbolWidth = ctrl.symbolWidth;
          dims.symbolHeight = ctrl.symbolHeight;
          return dims;
        }
      }
    }
    // Fallback to cached (still better than nothing)
    dims.symbolWidth = this.symbolWidth;
    dims.symbolHeight = this.symbolHeight;
    return dims;
  }

  // ── Grid Position Helper ────────────────────────────────────────────────────

  _getGridPosition(reelIndex, symbolIndex) {
    // Resolve from actual overlay container position (same as OrbActivator)
    const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
    const container = overlaySymbolRef?.getContainer?.();

    if (container && this.collectionLayerContainer && !this.collectionLayerContainer.destroyed) {
      const globalPos = container.toGlobal({ x: 0, y: 0 });
      const localPos = this.collectionLayerContainer.toLocal(globalPos);
      return { x: localPos.x, y: localPos.y };
    }

    // Fallback to math-based position
    const { symbolHeight: liveH } = this._getLiveDimensions();
    const centerOffset = (Settings.REEL_COUNT - 1) / 2;
    const x = this.reelSpacing * (reelIndex - centerOffset);
    const containerY = -liveH * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5);
    const y = containerY + liveH * symbolIndex;
    return { x, y };
  }

  // ── Coin Value Formatting ───────────────────────────────────────────────────

  _formatCoinValue(rawValue) {
    if (!rawValue) return "";
    const coinValue = typeof rawValue === "number" ? rawValue : parseInt(rawValue, 10);
    if (isNaN(coinValue) || coinValue <= 0) return "";
    // Values are already in subunits — just format, no bet multiplication
    return formatToCustomLocale(coinValue);
  }

  // ── Mystery Feature (GDD §15.1) ────────────────────────────────────────────

  /**
   * Execute Mystery feature — shuffle → stop → transform → swap to jackpot idle.
   * After this completes, the j spine shows the tier skin (e.g. c_mini) with idle.
   * Call flipRevealCoin() separately when ready to reveal the value.
   *
   * @param {Object} params
   * @param {number} params.reelIndex - Zeus position
   * @param {number} params.symbolIndex - Zeus position
   * @param {string} params.tier - "mini" | "minor" | "major"
   * @param {boolean} [params.slamStop=false]
   * @returns {Promise<void>}
   */
  async executeMystery({ reelIndex, symbolIndex, tier, slamStop = false }) {
    if (this._cancelled) return;

    const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
    if (!overlaySymbolRef) {
      return;
    }

    const stopAnim = TIER_TO_STOP[tier];
    const transformAnim = TIER_TO_TRANSFORM[tier];
    if (!stopAnim || !transformAnim) {
      return;
    }

    // Map tier to its stop sound
    const tierStopSound = {
      mini: SOUND_NAME.cm_stop_mini,
      minor: SOUND_NAME.cm_stop_minor,
      major: SOUND_NAME.cm_stop_major,
    }[tier];

    // Fast mode (turbo/slamStop): skip shuffle, straight to transform
    if (slamStop || this._isFastMode()) {
      soundManager.stop(SOUND_NAME.cm_shuffle);
      if (tierStopSound) soundManager.play(tierStopSound);
      soundManager.play(SOUND_NAME.cm_transform);
      await this._playAnimAndWait(overlaySymbolRef, transformAnim);
      this._layeredSpineSwap(overlaySymbolRef, TIER_TO_SYMBOL_ID[tier]);
      // Mystery revealed → demote to the revealed jackpot tier's z-index
      overlaySymbolRef.setZIndex?.(getSymbolZIndex(TIER_TO_SYMBOL_ID[tier]));
      return;
    }

    // Normal mode: full shuffle → stop → transform sequence
    soundManager.play(SOUND_NAME.cm_shuffle);
    await this._playAnimAndWait(overlaySymbolRef, MYSTERY_ANIMS.SHUFFLE);
    soundManager.stop(SOUND_NAME.cm_shuffle);
    if (this._cancelled) return;

    // Reactive fast mode check — if player pressed spacebar during shuffle, skip to result
    if (this._isFastMode()) {
      if (tierStopSound) soundManager.play(tierStopSound);
      soundManager.play(SOUND_NAME.cm_transform);
      await this._playAnimAndWait(overlaySymbolRef, transformAnim);
      this._layeredSpineSwap(overlaySymbolRef, TIER_TO_SYMBOL_ID[tier]);
      // Mystery revealed → demote to the revealed jackpot tier's z-index
      overlaySymbolRef.setZIndex?.(getSymbolZIndex(TIER_TO_SYMBOL_ID[tier]));
      return;
    }

    if (tierStopSound) soundManager.play(tierStopSound);
    await this._playAnimAndWait(overlaySymbolRef, stopAnim);
    if (this._cancelled) return;

    // Phase 2: transform + idle swap
    soundManager.play(SOUND_NAME.cm_transform);
    await this._playAnimAndWait(overlaySymbolRef, transformAnim);
    this._layeredSpineSwap(overlaySymbolRef, TIER_TO_SYMBOL_ID[tier]);
    // Mystery revealed → demote to the revealed jackpot tier's z-index
    overlaySymbolRef.setZIndex?.(getSymbolZIndex(TIER_TO_SYMBOL_ID[tier]));
  }

  /**
   * Flip a jackpot coin to reveal its value.
   * Plays double_value_rotate on the active j spine, injects value text,
   * then switches to default skin (clean coin, no tier label) and loops idle.
   *
   * Call this separately after executeMystery when ready to reveal the value.
   *
   * @param {Object} params
   * @param {number} params.reelIndex
   * @param {number} params.symbolIndex
   * @param {number} params.revealedValue - Raw coin value to display
   * @returns {Promise<void>}
   */
  async flipRevealCoin({ reelIndex, symbolIndex, revealedValue }) {
    if (this._cancelled) return;

    const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
    if (!overlaySymbolRef) return;

    // Guard: only flip once per coin
    const flipKey = `${reelIndex}_${symbolIndex}`;
    if (this._flippedCoins.has(flipKey)) return;
    this._flippedCoins.add(flipKey);

    const controller = overlaySymbolRef?.getController?.();

    const spine = controller?.getActiveSpine?.();
    if (!spine?.state) return;

    // Inject value text at alpha 0 — follows bone transforms during flip
    this._updateOverlayValueText(overlaySymbolRef, reelIndex, symbolIndex, revealedValue);
    this._setOverlayTextAlpha(overlaySymbolRef, 0);

    // Start flip, show text after 1 frame
    soundManager.play(SOUND_NAME.j_rotate);
    spine.state.setAnimation(0, "double_value_rotate", false);
    await new Promise(r => requestAnimationFrame(r));
    this._setOverlayTextAlpha(overlaySymbolRef, 1);

    // Resolve on value_ready (0.5s), complete handles skin swap + idle
    await new Promise((resolve) => {
      let resolved = false;
      let tid = null;
      const listener = {
        event: (entry, event) => {
          if (event.data.name === "value_ready") {
            safeResolve();
          }
        },
        complete: (entry) => {
          if (entry.animation.name === "double_value_rotate") {
            requestAnimationFrame(() => {
              this._switchToDefaultSkinIdle(overlaySymbolRef);
            });
            safeResolve();
          }
        },
      };
      const safeResolve = () => {
        if (resolved) return;
        resolved = true;
        if (tid != null) {
          tid.clear();
          this._pendingTimers.delete(tid);
        }
        try { spine.state.removeListener(listener); } catch (_) {}
        resolve();
      };
      try {
        spine.state.addListener(listener);
      } catch (err) {
        safeResolve();
      }
      // Safety fallback — tracked for cleanup on cancel()
      tid = new TickerTimeout(safeResolve, 3000);
      this._pendingTimers.add(tid);
    });
  }

  // ── Double Feature (GDD §15.2) ─────────────────────────────────────────────

  /**
   * Execute Double feature — doubles all coin values on the grid.
   *
   * @param {Object} params
   * @param {number} params.reelIndex - Green Zeus position
   * @param {number} params.symbolIndex - Green Zeus position
   * @param {Array<{reelIndex: number, symbolIndex: number, newValue: number}>} params.affectedCoins
   * @param {number} params.zeusValue - Green Zeus's own revealed value
   * @param {boolean} [params.slamStop=false]
   * @returns {Promise<void>}
   */
  async executeDouble({
    reelIndex,
    symbolIndex,
    affectedCoins,
    zeusValue,
    slamStop = false,
  }) {
    if (this._cancelled) return;

    const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
    if (!overlaySymbolRef) return;

    const fastMode = slamStop || this._isFastMode();

    // Step 1: Transition to LOOP_MULTIPLY.
    // The activate animation already played in OrbActivator (non-skippable).
    // 2x label clones are taken from loop_multiply state (2x is visible there).
    overlaySymbolRef.playNamedAnimation?.(DOUBLE_ANIMS.LOOP_MULTIPLY, true);

    // Step 2: Double each coin with lightning trail
    // NOTE: zeusPos resolved fresh per-trail below — not cached here.
    // Orientation/resize changes between trails are reflected immediately.

    // Sort coins: horizontal grid order (row-by-row, left-to-right)
    const sortedCoins = sortGridOrder(affectedCoins);

    // ── Prepare bolt data ──
    const preparedCoins = [];
    for (const coin of sortedCoins) {
      const coinOverlayReelRef = this.overlayReelRefs?.current?.[coin.reelIndex];
      const coinOverlaySymbolRef = coinOverlayReelRef?.getSymbolRef?.(coin.symbolIndex);
      const coinController = coinOverlaySymbolRef?.getController?.();
      let activeSpine = coinController?.activeSpineKey;

      // If this coin is still mid-transform ("cs"), await the pending mystery swap.
      if (activeSpine === "cs") {
        const transformKey = `${coin.reelIndex}_${coin.symbolIndex}`;
        const pendingTransform = this._pendingMysteryTransforms.get(transformKey);
        if (pendingTransform) {
          await pendingTransform;
          if (this._cancelled) return;
          activeSpine = coinController?.activeSpineKey;
        }
      }

      const hasVisibleText = !!(coinController?.coinValueText?.text);
      const isJackpotCoin = activeSpine === "j";
      const isZeusOrb = activeSpine === "cs";
      if (isZeusOrb && !hasVisibleText) {
        continue;
      }
      if (!isJackpotCoin && !hasVisibleText && (!coin.newValue || coin.newValue === 0)) {
        continue;
      }

      preparedCoins.push({ coin, activeSpine, hasVisibleText, isJackpotCoin });
    }

    // ── Fast mode: fire all trails simultaneously at normal speed ──
    if (fastMode && preparedCoins.length > 0) {

      // Flip-reveal any unrevealed jackpots simultaneously BEFORE trails
      const flipPromises = [];
      for (const { coin, isJackpotCoin, hasVisibleText } of preparedCoins) {
        if (isJackpotCoin && !hasVisibleText) {
          flipPromises.push(this.flipRevealCoin({
            reelIndex: coin.reelIndex, symbolIndex: coin.symbolIndex, revealedValue: coin.originalValue || Math.floor(coin.newValue / 2),
          }));
        }
      }
      if (flipPromises.length > 0) await Promise.all(flipPromises);

      // Play trail sound once for the entire batch (not per-coin)
      soundManager.play(SOUND_NAME.particles_fly);
      const boltPromises = [];
      for (let pi = 0; pi < preparedCoins.length; pi++) {
        if (this._cancelled) break;
        const { coin } = preparedCoins[pi];
        const coinPos = this._getGridPosition(coin.reelIndex, coin.symbolIndex);
        const coinKey = `${coin.reelIndex}_${coin.symbolIndex}`;
        const prevValue = this._previousValues.get(coinKey) || coin.originalValue || Math.floor(coin.newValue / 2);
        this._previousValues.set(coinKey, coin.newValue);

        // Build arrival callback — animate counter unless super turbo
        const onImpact = () => {
          this._animateCounter(
            coinKey, prevValue, coin.newValue, VALUE_TRAVEL_DURATION * 0.5,
            (currentVal) => this._updateCoinValue(coin.reelIndex, coin.symbolIndex, currentVal),
          );
        };

        // Fire trail at normal duration (simultaneous) with arrival callback
        const currentZeusPos = this._getGridPosition(reelIndex, symbolIndex);
        boltPromises.push(
          this._fireDoubleTrail(currentZeusPos, coinPos, VALUE_TRAVEL_DURATION, onImpact)
        );
        // Yield 1 frame between trail launches to spread work
        if (pi < preparedCoins.length - 1) {
          await new Promise(resolve => requestAnimationFrame(resolve));
        }
      }
      await Promise.all(boltPromises);
      await this._waitForCounters();
      soundManager.play(SOUND_NAME.cd_multiply);
      // Zeus value text is NOT set here — d_reveals action handles it
      // with the proper activate_value_green_cd animation + fade-in.
      // Transition from LOOP_MULTIPLY → IDLE
      overlaySymbolRef.queueNamedAnimation?.(DOUBLE_ANIMS.IDLE, true);
      return;
    }

    // ── Sequential mode (normal) ──

    for (let i = 0; i < preparedCoins.length; i++) {
      if (this._cancelled) return;

      // Reactive fast mode check mid-loop — if player pressed spacebar,
      // fire simultaneous trails for remaining coins
      if (this._isFastMode()) {

        // Flip-reveal any unrevealed jackpots simultaneously BEFORE trails
        const flipPromises = [];
        for (let j = i; j < preparedCoins.length; j++) {
          const { coin, isJackpotCoin, hasVisibleText } = preparedCoins[j];
          if (isJackpotCoin && !hasVisibleText) {
            flipPromises.push(this.flipRevealCoin({
              reelIndex: coin.reelIndex, symbolIndex: coin.symbolIndex, revealedValue: coin.originalValue || Math.floor(coin.newValue / 2),
            }));
          }
        }
        if (flipPromises.length > 0) await Promise.all(flipPromises);

        // Play trail sound once for the remaining batch
        soundManager.play(SOUND_NAME.particles_fly);
        const boltPromises = [];
        for (let j = i; j < preparedCoins.length; j++) {
          if (this._cancelled) break;
          const { coin } = preparedCoins[j];
          const coinKey = `${coin.reelIndex}_${coin.symbolIndex}`;
          const prevValue = this._previousValues.get(coinKey) || coin.originalValue || Math.floor(coin.newValue / 2);
          this._previousValues.set(coinKey, coin.newValue);
          const coinPos = this._getGridPosition(coin.reelIndex, coin.symbolIndex);

          const onImpact = () => {
            this._animateCounter(
              coinKey, prevValue, coin.newValue, VALUE_TRAVEL_DURATION * 0.5,
              (currentVal) => this._updateCoinValue(coin.reelIndex, coin.symbolIndex, currentVal),
            );
          };

          const reactiveZeusPos = this._getGridPosition(reelIndex, symbolIndex);
          boltPromises.push(
            this._fireDoubleTrail(reactiveZeusPos, coinPos, VALUE_TRAVEL_DURATION, onImpact)
          );
          // Yield 1 frame between trail launches
          if (j < preparedCoins.length - 1) {
            await new Promise(resolve => requestAnimationFrame(resolve));
          }
        }
        await Promise.all(boltPromises);
        await this._waitForCounters();
        soundManager.play(SOUND_NAME.cd_multiply);
        // Transition from LOOP_MULTIPLY → IDLE
        overlaySymbolRef.queueNamedAnimation?.(DOUBLE_ANIMS.IDLE, true);
        break;
      }

      const { coin, hasVisibleText, isJackpotCoin } = preparedCoins[i];

      // Sequential: await trail arrival, no extra delay
      // (trail await at line ~467 already gates next iteration)

      const coinPos = this._getGridPosition(coin.reelIndex, coin.symbolIndex);

      // Track previous value for counter animation
      const coinKey = `${coin.reelIndex}_${coin.symbolIndex}`;
      const prevValue = this._previousValues.get(coinKey) || coin.originalValue || Math.floor(coin.newValue / 2);

      // Jackpot coin without visible text: flip-reveal BEFORE trail
      // so the original value is shown first, then the 2x trail doubles it.
      // Matches turbo behavior (flip all → then fire trails).
      if (isJackpotCoin && !hasVisibleText) {
        await this.flipRevealCoin({
          reelIndex: coin.reelIndex,
          symbolIndex: coin.symbolIndex,
          revealedValue: coin.originalValue || Math.floor(coin.newValue / 2),
        });
      }

      // Build the impact callback — normal mode uses counter animation
      const onImpact = () => {
        soundManager.play(SOUND_NAME.cd_multiply);
        this._animateCounter(
          coinKey, prevValue, coin.newValue, VALUE_TRAVEL_DURATION * 0.5,
          (currentVal) => this._updateCoinValue(coin.reelIndex, coin.symbolIndex, currentVal),
        );
        this._previousValues.set(coinKey, coin.newValue);
      };

      // Play trail sound per-coin in sequential mode
      soundManager.play(SOUND_NAME.particles_fly);
      // Await the trail — onImpact fires on arrival at coin
      const seqZeusPos = this._getGridPosition(reelIndex, symbolIndex);
      await this._fireDoubleTrail(seqZeusPos, coinPos, VALUE_TRAVEL_DURATION, onImpact);

    }

    // Wait for the last coin's counter animation to finish before returning.
    // Without this, d_reveals starts immediately and the last counter gets cut short.
    await this._waitForCounters();

    // Transition from LOOP_MULTIPLY → IDLE (same pattern as collect's UPDATE_VALUE → IDLE).
    // Without this, the double coin stops on LOOP_MULTIPLY's last frame instead of looping idle.
    if (!this._cancelled) {
      overlaySymbolRef.queueNamedAnimation?.(DOUBLE_ANIMS.IDLE, true);
    }

    // Zeus value reveal is handled separately by d_reveals action
  }

  // ── Collect Feature (GDD §15.3) ────────────────────────────────────────────

  /**
   * Execute Collect feature — gather all coin values into Blue Zeus.
   *
   * RC pattern: staggered launches, each coin arrival increments Zeus value.
   * 1. First coin lands → create text with coin's value
   * 2. Each subsequent coin → add its value to running total
   *
   * @param {Object} params
   * @param {number} params.reelIndex - Blue Zeus position
   * @param {number} params.symbolIndex - Blue Zeus position
   * @param {Array<{reelIndex: number, symbolIndex: number, value: number}>} params.sourceCoins
   * @param {number} params.accumulatedValue - Total value after collection
   * @param {boolean} [params.slamStop=false]
   * @returns {Promise<void>}
   */
  async executeCollect({
    reelIndex,
    symbolIndex,
    sourceCoins,
    accumulatedValue,
    slamStop = false,
  }) {
    if (this._cancelled) return;

    const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
    if (!overlaySymbolRef) {
      return;
    }

    // NOTE: zeusPos resolved fresh per-trail below — not cached here.
    // Orientation/resize changes between trails are reflected immediately.
    const fastMode = slamStop || this._isFastMode();

    // Sort coins: horizontal grid order (row-by-row, left-to-right)
    // Done BEFORE the fast/normal branch so both paths use identical order.
    // Matches executeDouble which sorts at line 341 before its branch.
    const orderedCoins = sortGridOrder(sourceCoins);

    if (fastMode) {
      // Slam stop: all trails fire simultaneously, values animate on arrival

      let runningTotal = 0;
      let textCreated = false;
      const zeusKey = `zeus_${reelIndex}_${symbolIndex}`;

      // Flip-reveal any unrevealed jackpots simultaneously
      const flipPromises = [];
      for (const coin of orderedCoins) {
        const coinOverlay = this.overlayReelRefs?.current?.[coin.reelIndex]?.getSymbolRef?.(coin.symbolIndex);
        const coinCtrl = coinOverlay?.getController?.();
        if (coinCtrl?.activeSpineKey === "j" && !(coinCtrl?.coinValueText?.text)) {
          flipPromises.push(this.flipRevealCoin({
            reelIndex: coin.reelIndex,
            symbolIndex: coin.symbolIndex,
            revealedValue: coin.value,
          }));
        }
      }
      if (flipPromises.length > 0) {
        await Promise.all(flipPromises);
      }

      if (this.valueTraveler && orderedCoins.length > 0) {
        const { symbolHeight: liveH } = this._getLiveDimensions();
        const fontSize = liveH * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        const textStyle = new PIXI.TextStyle(scaleStyle(FONT_STYLES.coinValue, fontSize));
        // Play trail sound once for the entire batch (not per-coin)
        soundManager.play(SOUND_NAME.particles_fly);
        const trailPromises = [];
        for (let ci = 0; ci < orderedCoins.length; ci++) {
          if (this._cancelled) break;
          const coin = orderedCoins[ci];
          const fromPos = this._getGridPosition(coin.reelIndex, coin.symbolIndex);
          const displayValue = this._formatCoinValue(coin.value);
          const tsZeusPos = this._getGridPosition(reelIndex, symbolIndex);
          trailPromises.push(
            this.valueTraveler.travelWithTrail({
              text: displayValue, style: textStyle,
              from: fromPos, to: tsZeusPos,
              duration: VALUE_TRAVEL_DURATION,
              coinValue: coin.value,
              // eslint-disable-next-line no-loop-func
              onArrival: () => {
                const previousTotal = runningTotal;
                runningTotal += coin.value;

                if (!textCreated) {
                  textCreated = true;
                  this._updateOverlayValueText(overlaySymbolRef, reelIndex, symbolIndex, runningTotal);
                  this._setOverlayTextAlpha(overlaySymbolRef, 1);
                }

                this._animateCounter(
                  zeusKey, previousTotal, runningTotal, VALUE_TRAVEL_DURATION * 0.5,
                  (currentVal) => this._setOverlayText(overlaySymbolRef, formatToCustomLocale(currentVal)),
                );
                // No per-arrival UPDATE_VALUE — fires once after all trails (avoids N rapid restarts)
              },
            })
          );
          // Yield 1 frame between trail launches
          if (ci < orderedCoins.length - 1) {
            await new Promise(resolve => requestAnimationFrame(resolve));
          }
        }
        await Promise.all(trailPromises);
        await this._waitForCounters();
        // Play UPDATE_VALUE once after all trails settle, then idle
        soundManager.play(SOUND_NAME.cc_update);
        overlaySymbolRef.playNamedAnimation?.(COLLECT_ANIMS.UPDATE_VALUE, false);
      }

      // Queue idle after UPDATE_VALUE finishes naturally (smooth transition)
      overlaySymbolRef.queueNamedAnimation?.(COLLECT_ANIMS.IDLE, true);
      return;
    }

    // The activate animation already played in OrbActivator (non-skippable).
    // Go straight to collecting values.
    // orderedCoins already sorted at the top of executeCollect.

    const _getSpineForCollect = (coin) =>
      this.overlayReelRefs?.current?.[coin.reelIndex]
        ?.getSymbolRef?.(coin.symbolIndex)
        ?.getController?.()?.activeSpineKey;

    if (this.valueTraveler && orderedCoins.length > 0) {
      const { symbolHeight: liveH } = this._getLiveDimensions();
      const fontSize    = liveH * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
      const zeusKey     = `zeus_${reelIndex}_${symbolIndex}`;
      let runningTotal  = 0;
      let textCreated   = false;

      // Cache TextStyle once — fontSize is identical for all coins in this collection run
      const textStyle    = new PIXI.TextStyle(scaleStyle(FONT_STYLES.coinValue, fontSize));

      // Build a traveler config for a single coin (called per-coin, so post-flip state is current)
      const buildConfig = (coin) => {
        const fromPos      = this._getGridPosition(coin.reelIndex, coin.symbolIndex);
        const toPos        = this._getGridPosition(reelIndex, symbolIndex);
        const displayValue = this._formatCoinValue(coin.value);
        return {
          text: displayValue, style: textStyle,
          from: fromPos, to: toPos,
          duration: VALUE_TRAVEL_DURATION,
          coinValue: coin.value,
        };
      };

      // Shared arrival handler — increments Zeus counter on each coin landing
      const onArrival = (config) => {
        const previousTotal = runningTotal;
        runningTotal += config.coinValue;

        if (!textCreated) {
          textCreated = true;
          this._updateOverlayValueText(overlaySymbolRef, reelIndex, symbolIndex, runningTotal);
          this._setOverlayTextAlpha(overlaySymbolRef, 1);
        }

        this._animateCounter(
          zeusKey, previousTotal, runningTotal, VALUE_TRAVEL_DURATION * 0.5,
          (currentVal) => this._setOverlayText(overlaySymbolRef, formatToCustomLocale(currentVal)),
        );
        // UPDATE_VALUE per-arrival only in sequential mode.
        // Fast mode plays it once after all trails (avoids N rapid restarts).
        if (!this._isFastMode()) {
          soundManager.play(SOUND_NAME.cc_update);
          overlaySymbolRef.playNamedAnimation?.(COLLECT_ANIMS.UPDATE_VALUE, false);
        }
      };

      // ── Process all coins sequentially in grid order ─────────────────────

      for (let ci = 0; ci < orderedCoins.length; ci++) {
        const coin = orderedCoins[ci];
        if (this._cancelled) return;

        // Reactive fast mode check mid-loop — if player pressed spacebar,
        // fire simultaneous trails for remaining coins
        if (this._isFastMode()) {

          // Flip-reveal any unrevealed jackpots simultaneously
          const flipPromises = [];
          for (let j = ci; j < orderedCoins.length; j++) {
            const remainCoin = orderedCoins[j];
            const coinOverlay = this.overlayReelRefs?.current?.[remainCoin.reelIndex]?.getSymbolRef?.(remainCoin.symbolIndex);
            const coinCtrl = coinOverlay?.getController?.();
            if (coinCtrl?.activeSpineKey === "j" && !(coinCtrl?.coinValueText?.text)) {
              flipPromises.push(this.flipRevealCoin({
                reelIndex: remainCoin.reelIndex,
                symbolIndex: remainCoin.symbolIndex,
                revealedValue: remainCoin.value,
              }));
            }
          }
          if (flipPromises.length > 0) {
            await Promise.all(flipPromises);
          }

          // Fire remaining trails simultaneously at normal duration
          soundManager.play(SOUND_NAME.particles_fly);
          const trailPromises = [];
          for (let j = ci; j < orderedCoins.length; j++) {
            if (this._cancelled) break;
            const remainCoin = orderedCoins[j];
            const config = buildConfig(remainCoin);
            trailPromises.push(
              this.valueTraveler.travelWithTrail({
                ...config,
                onArrival: () => onArrival(config),
              })
            );
            // Yield 1 frame between trail launches
            if (j < orderedCoins.length - 1) {
              await new Promise(resolve => requestAnimationFrame(resolve));
            }
          }
          await Promise.all(trailPromises);
          await this._waitForCounters();
          // Play UPDATE_VALUE once after all trails settle
          soundManager.play(SOUND_NAME.cc_update);
          overlaySymbolRef.playNamedAnimation?.(COLLECT_ANIMS.UPDATE_VALUE, false);
          break;
        }

        let spine = _getSpineForCollect(coin);

        // If still mid-transform ("cs"), await the pending mystery swap
        if (spine === "cs") {
          const transformKey = `${coin.reelIndex}_${coin.symbolIndex}`;
          const pendingTransform = this._pendingMysteryTransforms.get(transformKey);
          if (pendingTransform) {
            await pendingTransform;
            if (this._cancelled) return;
            spine = _getSpineForCollect(coin);
          }
        }

        if (spine === "j") {
          // Mystery-transformed jackpot: flip-reveal before collecting.
          const coinOverlay = this.overlayReelRefs?.current?.[coin.reelIndex]?.getSymbolRef?.(coin.symbolIndex);
          const coinCtrl = coinOverlay?.getController?.();
          const alreadyRevealed = !!(coinCtrl?.coinValueText?.text);
          if (!alreadyRevealed) {
            await this.flipRevealCoin({
              reelIndex: coin.reelIndex,
              symbolIndex: coin.symbolIndex,
              revealedValue: coin.value,
            });
          }
        }

        if (this._cancelled) return;

        // Sequential: await trail arrival below, no extra delay

        // Play trail sound per-coin in sequential mode
        soundManager.play(SOUND_NAME.particles_fly);
        // Now the coin shows its value — collect it
        const config = buildConfig(coin);
        await this.valueTraveler.travelWithTrail({
          ...config,
          onArrival: () => onArrival(config),
        });

      }

      // Wait for the last coin's counter animation to finish before proceeding.
      await this._waitForCounters();

      // Queue idle after last UPDATE_VALUE finishes naturally (smooth transition)
      if (!this._cancelled) {
        overlaySymbolRef.queueNamedAnimation?.(COLLECT_ANIMS.IDLE, true);
      }
    }

    if (this._cancelled) return;

  }

  // ── Utils ────────────────────────────────────────────────────────────────────

  // ── 2x Label Spine Pool ────────────────────────────────────────────────────
  //
  // Double trails need a spine showing the "2×" label. Creating a Spine.from()
  // per trail is expensive (atlas parse, slot setup, per-frame callbacks).
  // Pool spines: acquire before trail, release on arrival for reuse.
  //
  // ValueTraveler calls wrapper.destroy({ children: true }) BEFORE onArrival,
  // which would destroy the pooled spine. To prevent this, we wrap the spine in
  // a "shield" container that removes the spine from itself on destroy, letting
  // the wrapper destruction cascade harmlessly while the spine survives.

  /**
   * Acquire a 2x label spine — from pool if available, otherwise create new.
   * @returns {Spine|null}
   */
  _acquire2xLabel() {
    // Lazy-init pool
    if (!this._2xLabelPool) this._2xLabelPool = [];

    if (this._2xLabelPool.length > 0) {
      const spine = this._2xLabelPool.pop();
      // Reset for reuse
      spine.alpha = 1;
      spine.autoUpdate = true;
      spine.state.setAnimation(0, DOUBLE_ANIMS.LOOP_MULTIPLY, true);
      spine.state.update(0);
      spine.state.apply(spine.skeleton);
      spine.skeleton.updateWorldTransform(0);
      // Update scale in case of resize since last use
      const nativeHeight = Settings.SYMBOL_HEIGHT || 266;
      spine.scale.set(this._getLiveDimensions().symbolHeight / nativeHeight);
      return spine;
    }

    return this._create2xLabel();
  }

  /**
   * Pre-warm the 2x label pool by creating one spine ahead of time.
   * Called at the start of the activation phase so the expensive Spine.from()
   * cold-start is paid while the first (Mystery) orb's bolt/flash is playing,
   * instead of hitting cold on the Double orb during an otherwise-still frame.
   * Idempotent + guarded: no-op if cancelled or the pool already has a spine.
   */
  prewarmDoubleLabel() {
    if (this._cancelled) return;
    if (!this._2xLabelPool) this._2xLabelPool = [];
    if (this._2xLabelPool.length > 0) return;
    const spine = this._create2xLabel();
    if (spine) this._release2xLabel(spine);
  }

  /**
   * Return a 2x label spine to the pool for reuse.
   * @param {Spine} spine
   */
  _release2xLabel(spine) {
    if (!spine || spine.destroyed) return;
    if (!this._2xLabelPool) this._2xLabelPool = [];
    // Detach from any parent (shield container already removed it, but guard)
    if (spine.parent) spine.parent.removeChild(spine);
    spine.autoUpdate = false;
    spine.alpha = 0;
    this._2xLabelPool.push(spine);
  }

  /**
   * Create a "shield" container that protects a pooled spine from
   * wrapper.destroy({ children: true }). On destroy, the shield removes
   * the spine from itself first, then self-destructs harmlessly.
   * @param {Spine} spine - The pooled spine to protect
   * @returns {PIXI.Container} Shield container (pass as displayObject to travelWithTrail)
   */
  _createShieldContainer(spine) {
    const shield = new PIXI.Container();
    shield.addChild(spine);
    // Override destroy to rescue the spine before cascading
    const originalDestroy = shield.destroy.bind(shield);
    shield.destroy = (options) => {
      if (spine.parent === shield) {
        shield.removeChild(spine);
      }
      // Release spine back to pool
      this._release2xLabel(spine);
      originalDestroy(options);
    };
    return shield;
  }

  /**
   * Create a mini spine clone showing only the "2×" label from the cd (Double) skin.
   * Used as the traveling display object for Double feature trails.
   * @returns {Spine|null} A spine instance with only 2x slots visible, or null on failure
   */
  _create2xLabel() {
    try {
      const assets = PIXI.Assets.cache;
      if (!assets.has("cs-skeleton") || !assets.has("cs-atlas")) return null;

      const spine = Spine.from({ skeleton: "cs-skeleton", atlas: "cs-atlas" });
      if (!spine?.skeleton || !spine?.state) return null;

      // Apply cd (double) skin
      const cdSkin = spine.skeleton.data.findSkin("cd");
      if (cdSkin) {
        spine.skeleton.setSkin(cdSkin);
        spine.skeleton.setSlotsToSetupPose();
      }

      // Step 1: autoUpdate = false to prevent reset-pose flash (RC pattern)
      spine.autoUpdate = false;

      // Step 2: Set animation — use loop_multiply where 2x is visible from frame 0
      spine.state.setAnimation(0, DOUBLE_ANIMS.LOOP_MULTIPLY, true);

      // Step 3: Force-apply skeleton state for first render (RC SpineCloner pattern)
      spine.state.update(0);
      spine.state.apply(spine.skeleton);

      // Step 4: Null non-2x slot attachments AFTER apply
      const slots = spine.skeleton.slots;
      for (const slot of slots) {
        if (!DOUBLE_2X_SLOTS.has(slot.data.name)) {
          slot.setAttachment(null);
        }
      }

      // Force world transform so first render is correct
      spine.skeleton.updateWorldTransform(0);

      // Step 5: Register per-frame callback to re-null non-2x slots
      // (animation keyframes restore attachments on every state.apply).
      // Precompute the non-2x slot list once — it's constant for this spine's
      // lifetime, so the callback avoids a full slot scan + Set lookup per frame.
      const slotsToNull = spine.skeleton.slots.filter(
        (slot) => !DOUBLE_2X_SLOTS.has(slot.data.name)
      );
      spine.beforeUpdateWorldTransforms = () => {
        for (let i = 0; i < slotsToNull.length; i++) {
          slotsToNull[i].setAttachment(null);
        }
      };

      // Step 6: Now safe to auto-update
      spine.autoUpdate = true;
      spine.alpha = 1;

      // Scale to match on-screen spine size (same as RC CoinCollectAnimator)
      const nativeHeight = Settings.SYMBOL_HEIGHT || 266;
      const { symbolHeight: liveH } = this._getLiveDimensions();
      const scale = liveH / nativeHeight;
      spine.scale.set(scale);

      return spine;
    } catch (e) {

      return null;
    }
  }

  /**
   * Fire a Double trail from Zeus to a target coin using the 2x label spine + trail.
   * Uses pooled spine wrapped in a shield container to survive ValueTraveler's
   * wrapper.destroy({ children: true }) — spine is returned to pool on arrival.
   * @param {{x,y}} fromPos - Green Zeus position
   * @param {{x,y}} toPos - Target coin position
   * @param {number} duration - Trail duration in ms
   * @param {Function} [onArrival] - Called when 2x arrives at the coin
   * @returns {Promise<void>}
   */
  _fireDoubleTrail(fromPos, toPos, duration, onArrival = null) {
    if (this._cancelled) return Promise.resolve();
    if (!this.valueTraveler) {
      onArrival?.();
      return Promise.resolve();
    }

    const spine = this._acquire2xLabel();
    // Wrap in shield container so wrapper.destroy doesn't kill the pooled spine
    const displayObject = spine ? this._createShieldContainer(spine) : null;

    // Animate scale: full spine size → coin value text size
    // The spine inside wrapper is already scaled to match on-screen size.
    // The 2x label fills ~35% of the spine height.
    // Coin value text = symbolHeight × FONT_SIZE_MULTIPLIER (0.25).
    // Target wrapper scale = fontSizeRatio / label proportion ≈ 0.25/0.35 ≈ 0.7
    const fontMult = Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER || 0.25;
    const scaleTo = fontMult / 0.35;

    return this.valueTraveler.travelWithTrail({
      from: fromPos,
      to: toPos,
      duration,
      displayObject,
      scaleFrom: 1.0,
      scaleTo,
      onArrival: () => onArrival?.(),
    });
  }

  /**
   * Check if fast mode is active (slam stop).
   * Slam stop is per-feature: each queued feature starts in normal mode
   * and only fast-forwards if the player presses spacebar during that feature.
   * @returns {boolean}
   */
  _isFastMode() {
    return !!this._spinEngine?._slamStopRequested;
  }

  // ── Value Update Helpers ────────────────────────────────────────────────────

  /**
   * Direct text setter for pre-created overlay text.
   * Takes an already-formatted display string (e.g., "0.25", "1.50").
   * Mirrors RC pattern: bitmapText.text = formatToCustomLocale(value).
   * No guards, no creation — text slot must already exist (from playNamedAnimation).
   *
   * Calls autoFit on every update to stay responsive to dimension changes
   * (resize can happen mid-animation, changing font size and container width).
   */
  _setOverlayText(overlaySymbolRef, displayText) {
    const controller = overlaySymbolRef?.getController?.();
    if (!controller?.coinValueText || controller.coinValueText.destroyed) return;
    controller.coinValueText.text = displayText;
    controller.autoFitCoinValueText();
  }

  /**
   * Update the overlay spine's text slot with a new coin value.
   * If coinValueText doesn't exist yet (e.g., Blue Zeus never had a value),
   * create it and attach to the spine's text slot.
   */
  _updateOverlayValueText(overlaySymbolRef, reelIndex, symbolIndex, rawValue) {
    const displayValue = this._formatCoinValue(rawValue);
    if (!displayValue) return;

    const controller = overlaySymbolRef?.getController?.();
    if (!controller) return;

    if (controller.coinValueText && !controller.coinValueText.destroyed) {
      // Text slot already exists (pre-created in playNamedAnimation) — just update
      controller.coinValueText.text = displayValue;
      controller.autoFitCoinValueText();
    } else {
      // Safety fallback: text doesn't exist yet. This shouldn't happen in normal
      // flow (playNamedAnimation pre-creates it), but guard against edge cases.

      const result = controller.createCoinValueText(displayValue);
      if (!result) return;

      controller.coinValueContainer = result.counterContainer;
      controller.coinValueText = result.text;

      // Attach to the spine's text slot
      const spineInstance = controller.getActiveSpine();
      controller._coinValueSpine = spineInstance;

      if (spineInstance) {
        const slotName = controller._getTextSlotName(controller.currentSymbolId);
        if (slotName) {
          const spineScale = spineInstance.scale.x || 1;
          const slot = spineInstance.skeleton?.findSlot(slotName);
          const slotBone = slot?.bone;
          const boneBaseScale = Math.abs(slotBone?.a) || 1;
          const totalScale = spineScale * boneBaseScale;

          if (totalScale && totalScale !== 0) {
            const baseFontSize = controller.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
            const scaledFontSize = baseFontSize / totalScale;
            const baseStyle = FONT_STYLES.coinValue;
            Object.assign(controller.coinValueText.style, scaleStyle(baseStyle, scaledFontSize));
            controller.coinValueText.scale.set(1);

            const maxTextWidth = (controller.symbolWidth * (1 - 0.05 * 2)) / totalScale;
            if (controller.coinValueText.width > maxTextWidth) {
              const scaleRatio = maxTextWidth / controller.coinValueText.width;
              controller.coinValueText.scale.x = scaleRatio;
            }
          }

          spineInstance.addSlotObject(slotName, controller.coinValueContainer);
        }
      }
    }
  }

  /**
   * Update a coin's value on both the static texture and overlay.
   */
  _updateCoinValue(reelIndex, symbolIndex, newValue) {
    // Update static symbol's value text
    const reelRef = this.reelRefs?.current?.[reelIndex];
    const symbolRef = reelRef?.getSymbolRef?.(symbolIndex);
    if (symbolRef?.current?.updateSymbolTexture) {
      // PC symbols expose updateSymbolTexture which re-renders with new value
      // For now, we update the overlay controller text directly
    }

    // Update overlay controller text
    const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
    const controller = overlaySymbolRef?.getController?.();

    if (controller?.coinValueText && !controller.coinValueText.destroyed) {
      const displayValue = this._formatCoinValue(newValue);
      if (displayValue) {
        controller.coinValueText.text = displayValue;
        controller.autoFitCoinValueText();
      }
    }
  }

  // ── Animation Helpers ───────────────────────────────────────────────────────

  /**
   * Layered spine swap: show the new spine on top first, then RAF hide the old.
   * Prevents visual gap between hiding old and showing new (same pattern as
   * useLandingAnimations static→spine swap).
   *
   * @param {Object} overlaySymbolRef - Overlay symbol imperative ref
   * @param {number|null} newSymbolId - Target symbol ID for showIdleAnimation
   */
  _layeredSpineSwap(overlaySymbolRef, newSymbolId) {
    if (!newSymbolId) return;
    const controller = overlaySymbolRef?.getController?.();
    if (!controller) return;

    const prevKey = controller.activeSpineKey; // e.g., "cs"

    // Temporarily clear activeSpineKey so showIdleAnimation doesn't hide cs
    controller.activeSpineKey = null;

    // Show j spine win → idle on top (cs still visible underneath)
    soundManager.play(SOUND_NAME.j_win);
    overlaySymbolRef.showWinAnimation(newSymbolId, false);

    // RAF: hide cs on next render frame — no visual gap
    if (prevKey) {
      requestAnimationFrame(() => {
        const prevSpine = controller.prePositionedSpines.get(prevKey);
        if (prevSpine) {
          prevSpine.alpha = 0.001;
          prevSpine.autoUpdate = false;
          if (prevSpine.state) prevSpine.state.clearTracks();
        }
      });
    }
  }

  /**
   * Set the alpha on the overlay's injected coin value text.
   * Used to hide text after mystery reveal (alpha 0) or show it for double (alpha 1).
   */
  _setOverlayTextAlpha(overlaySymbolRef, alpha) {
    const controller = overlaySymbolRef?.getController?.();
    if (!controller?.coinValueText || controller.coinValueText.destroyed) return;
    controller.coinValueText.alpha = alpha;
    if (controller.coinValueContainer && !controller.coinValueContainer.destroyed) {
      controller.coinValueContainer.alpha = alpha;
    }
  }

  /**
   * Switch the active j spine to 'default' skin and loop idle.
   * The default skin is a clean coin without MINI/MAJOR/MINOR label,
   * so only our injected value text is visible.
   */
  _switchToDefaultSkinIdle(overlaySymbolRef) {
    const controller = overlaySymbolRef?.getController?.();
    const spine = controller?.getActiveSpine();
    if (!spine?.skeleton || !spine?.state) return;

    requestAnimationFrame(() => {
      spine.skeleton.setSkinByName("default");
      spine.skeleton.setSlotsToSetupPose();
      spine.state.setAnimation(0, "idle", true);
    });
  }

  /**
   * Play a named animation and wait for completion.
   * If looping, waits for `maxDuration` ms then moves on.
   */
  _playAnimAndWait(overlaySymbolRef, animName, loop = false, maxDuration = 2000) {
    if (this._cancelled) return Promise.resolve();

    return new Promise((resolve) => {
      if (!overlaySymbolRef?.playNamedAnimation) {
        resolve();
        return;
      }

      let resolved = false;
      const done = () => {
        if (resolved) return;
        resolved = true;
        tid.clear();
        this._pendingTimers.delete(tid);
        resolve();
      };

      if (loop) {
        overlaySymbolRef.playNamedAnimation(animName, true);
      } else {
        overlaySymbolRef.playNamedAnimation(animName, false, done);
      }
      // Safety timeout — cleared if animation completes naturally
      const tid = new TickerTimeout(done, maxDuration);
      this._pendingTimers.add(tid);
    });
  }

  /**
   * Play an animation on the currently active spine (not hardcoded to cs).
   * Used after mystery→jackpot swap when j is the active spine.
   */
  _playActiveSpineAnim(overlaySymbolRef, animName, loop = false, maxDuration = 3000) {
    if (this._cancelled) return Promise.resolve();

    return new Promise((resolve) => {
      const controller = overlaySymbolRef?.getController?.();
      const spine = controller?.getActiveSpine();
      if (!spine?.state) { resolve(); return; }

      const entry = spine.state.setAnimation(0, animName, loop);
      if (!entry) { resolve(); return; }

      let resolved = false;
      const done = () => {
        if (resolved) return;
        resolved = true;
        tid.clear();
        this._pendingTimers.delete(tid);
        if (listenerEntry) {
          spine.state.removeListener(listenerEntry.listener);
          const idx = this._pendingListeners.indexOf(listenerEntry);
          if (idx !== -1) this._pendingListeners.splice(idx, 1);
        }
        resolve();
      };

      let listenerEntry = null;
      if (loop) {
        // For loops, just wait for maxDuration
      } else {
        const listener = { complete: () => done() };
        listenerEntry = { spine, listener };
        this._pendingListeners.push(listenerEntry);
        spine.state.addListener(listener);
      }
      // Safety timeout — cleared if animation completes naturally
      const tid = new TickerTimeout(done, maxDuration);
      this._pendingTimers.add(tid);
    });
  }

  // ── Counter Animation (mirrors RC RoyalCollectControlContext counterTicker) ──

  /**
   * Animate a value counter from previousValue to newValue using a PIXI.Ticker.
   * EaseOutQuad interpolation — same curve as RC.
   *
   * If a ticker for the same key is already running, it is cancelled first
   * (handles rapid coin arrivals where the previous counter hasn't finished).
   *
   * @param {string} key - Unique key for this counter (e.g., "zeus_2_1" or "3_0")
   * @param {number} previousValue - Start value (raw, not formatted)
   * @param {number} newValue - Target value (raw, not formatted)
   * @param {number} duration - Animation duration in ms
   * @param {Function} onUpdate - Called each frame with (currentValue: number)
   */
  _animateCounter(key, previousValue, newValue, duration, onUpdate) {
    // Cancel any in-flight counter for this key (RC line 408-412)
    this._cancelCounter(key);

    // Duration 0 or negative: instant update
    if (duration <= 0) {
      onUpdate(newValue);
      return;
    }

    let elapsed = 0;

    const tickerFn = () => {
      elapsed += PIXI.Ticker.shared.deltaMS;
      const t = Math.min(elapsed / duration, 1);
      // EaseOutQuad: 1 - (1 - t)² — same as RC line 432
      const eased = 1 - (1 - t) * (1 - t);
      const currentValue = Math.floor(
        previousValue + (newValue - previousValue) * eased,
      );

      onUpdate(currentValue);

      if (t >= 1) {
        // Ensure final value is exact
        onUpdate(newValue);
        PIXI.Ticker.shared.remove(tickerFn);
        this._counterTickers.delete(key);
      }
    };

    this._counterTickers.set(key, tickerFn);
    PIXI.Ticker.shared.add(tickerFn);
  }

  /**
   * Cancel an in-flight counter ticker by key.
   */
  _cancelCounter(key) {
    const prevTicker = this._counterTickers.get(key);
    if (prevTicker) {
      PIXI.Ticker.shared.remove(prevTicker);
      this._counterTickers.delete(key);
    }
  }

  /**
   * Wait for all counter tickers to finish before proceeding.
   * Mirrors RC's waitForCounters pattern (poll at 50ms intervals).
   * @returns {Promise<void>}
   */
  _waitForCounters() {
    if (this._counterTickers.size === 0) return Promise.resolve();
    return new Promise((resolve) => {
      const check = () => {
        if (this._cancelled || this._counterTickers.size === 0) {
          resolve();
        } else {
          const tid = new TickerTimeout(() => {
            this._pendingTimers.delete(tid);
            check();
          }, 50);
          this._pendingTimers.add(tid);
        }
      };
      check();
    });
  }

  // ── Resize / Cleanup ────────────────────────────────────────────────────────

  resize(symbolWidth, symbolHeight, reelSpacing) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;
    if (this.valueTraveler) {
      this.valueTraveler.resize(symbolWidth, symbolHeight);
    }
  }

  cancel() {
    this._cancelled = true;
    if (this.valueTraveler) this.valueTraveler.cancel();

    // Clear all counter tickers (RC line 1312-1316)
    for (const tickerFn of this._counterTickers.values()) {
      PIXI.Ticker.shared.remove(tickerFn);
    }
    this._counterTickers.clear();
    this._previousValues.clear();
    this._flippedCoins.clear();
    this._pendingMysteryTransforms.clear();

    // Clear all pending TickerTimeouts (prevents leaked callbacks after cancel)
    for (const tid of this._pendingTimers) {
      if (tid && typeof tid.clear === "function") tid.clear();
    }
    this._pendingTimers.clear();

    // Remove all pending spine listeners
    for (const { spine, listener } of this._pendingListeners) {
      try { spine?.state?.removeListener(listener); } catch (_) {}
    }
    this._pendingListeners = [];

    // Destroy pooled 2x label spines (active ones are rescued by shield containers
    // during valueTraveler.cancel() above, then returned to pool — destroy them too)
    if (this._2xLabelPool) {
      for (const spine of this._2xLabelPool) {
        if (!spine.destroyed) {
          try { spine.destroy(); } catch (_) {}
        }
      }
      this._2xLabelPool = [];
    }
  }

  destroy() {
    this.cancel();
    if (this.valueTraveler) {
      this.valueTraveler.destroy();
      this.valueTraveler = null;
    }
    this._spinEngine = null;
    this.overlayReelRefs = null;
    this.reelRefs = null;
  }
}

export default PotCollectionFeatureExecutor;
